'use client'
import * as React from 'react'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'

import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'

import { Text } from '../../components/text.js'
import { Heading } from '../../components/heading.js'
import { forwardRef } from 'react'

import ProductCard from './ProductCard.js'
import type {
  ProductCardData,
  ProductCardProps,
  ButtonFunctionType,
  ImageOverlayShelfSemanticName,
  CopyConfig,
} from './types.js'
import { useExposure } from '../../hooks/useExposure.js'

const componentType = 'shelf'
const componentName = 'image_overlay_shelf'

// Re-export types for external use
export type {
  ButtonFunctionType,
  ProductCardData,
  ProductCardProps,
  ImageOverlayShelfSemanticName,
  CopyConfig,
  ImageObjectPosition,
} from './types.js'

/**
 * ImageOverlayShelf 业务组件数据接口
 */
export interface ImageOverlayShelfData {
  /** 组件标题 */
  title?: string
  /** 组件描述 */
  description?: string
  /** 产品列表 */
  productsCard: ProductCardData[]
  /** 是否显示标签 */
  showTags?: boolean
  /** 是否显示原价 */
  showOriginalPrice?: boolean
  /** 了解更多按钮文本 */
  secondaryButtonText?: string
  /** 了解更多按钮功能 */
  secondaryButtonFun?: ButtonFunctionType
  /** 立即购买按钮文本 */
  primaryButtonText?: string
  /** 立即购买按钮功能 */
  primaryButtonFun?: ButtonFunctionType
}

/**
 * ImageOverlayShelf Props 接口
 */
export interface ImageOverlayShelfProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 业务数据 */
  data: ImageOverlayShelfData
  /** 点击了解更多回调 */
  onLearnMore?: (product: ProductCardData) => void
  /** 点击立即购买回调 */
  onShopNow?: (product: ProductCardData) => void
  /** 点击加入购物车回调 */
  onAddToCart?: (product: ProductCardData) => void
  /** 点击产品图片回调 */
  onProductImageClick?: (product: ProductCardData) => void
  /** 语义化类名 */
  classNames?: Partial<Record<ImageOverlayShelfSemanticName, string>>
  /** 样式类名 */
  className?: string
  /** 文案配置 */
  copy?: CopyConfig
  /** 断点配置 */
  breakpoints?: {
    [key: number]: {
      spaceBetween: number
      freeMode: boolean
      slidesPerView: number
    }
  }
}

/**
 * ImageOverlayShelf 复合组件类型定义
 */
export interface ImageOverlayShelfComponent extends React.ForwardRefExoticComponent<
  ImageOverlayShelfProps & React.RefAttributes<HTMLDivElement>
> {
  /** 独立的 ProductCard 组件 */
  ProductCard: typeof ProductCard
}

/**
 * 共享 Props 类型（透传给 ProductCard）
 */
type SharedProductCardProps = Omit<ProductCardProps, 'product' | 'className'>

const ImageOverlayShelf = forwardRef<HTMLDivElement, ImageOverlayShelfProps>(
  (
    {
      data,
      onLearnMore,
      onShopNow,
      onAddToCart,
      onProductImageClick,
      classNames,
      copy,
      breakpoints,
      className = '',
      ...props
    },
    ref
  ) => {
    const boxRef = React.useRef<HTMLDivElement>(null)

    useExposure(boxRef, {
      componentType,
      componentName,
      componentTitle: data.title,
      componentDescription: data.description,
    })

    React.useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

    const {
      productsCard: products = [],
      showTags = true,
      showOriginalPrice = true,
      secondaryButtonText,
      secondaryButtonFun,
      primaryButtonText,
      primaryButtonFun,
    } = data

    // 构建共享 props（透传给 ProductCard）
    const sharedProps: SharedProductCardProps = {
      showTags,
      showOriginalPrice,
      secondaryButtonText,
      secondaryButtonFun,
      primaryButtonText,
      primaryButtonFun,
      onLearnMore,
      onShopNow,
      onAddToCart,
      onProductImageClick,
      classNames,
      copy,
    }

    // 默认断点配置
    const defaultBreakpoints = {
      0: {
        spaceBetween: 12,
        slidesPerView: 1.2,
      },
      768: {
        spaceBetween: 16,
        slidesPerView: products.length <= 2 ? 2 : 2.2,
      },
      1024: {
        spaceBetween: 16,
        slidesPerView: products.length <= 2 ? 2 : 3,
      },
      1440: {
        spaceBetween: 16,
        slidesPerView: Math.max(2, Math.min(4, products.length)),
      },
    }

    return (
      <div ref={boxRef} className={cn('collection-shelves-wrap w-full', className, classNames?.root)} {...props}>
        {/* 标题区域 */}
        {data.title && (
          <Heading as="h2" size={4} className={cn('text-info-primary mb-4', classNames?.title)}>
            {data.title}
          </Heading>
        )}
        {data.description && (
          <Text size={3} as="p" className={cn('text-info-primary mb-6', classNames?.description)}>
            {data.description}
          </Text>
        )}

        {/* Swiper 横向滑动布局 */}
        <Swiper
          spaceBetween={16}
          slidesPerView="auto"
          className="!overflow-visible"
          breakpoints={breakpoints || defaultBreakpoints}
        >
          {products.map((product, index) => (
            <SwiperSlide key={product.sku || index}>
              <ProductCard product={product} {...sharedProps} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    )
  }
)

ImageOverlayShelf.displayName = 'ImageOverlayShelf'

// 创建复合组件
const ImageOverlayShelfWithProductCard = ImageOverlayShelf as ImageOverlayShelfComponent
ImageOverlayShelfWithProductCard.ProductCard = ProductCard

export { ProductCard }
const ImageOverlayShelfWrapped = withLayout(ImageOverlayShelfWithProductCard)
export default ImageOverlayShelfWrapped
